import { useState } from "react";
import { C, Sheet, Btn, Badge, Stat, Topbar, BottomNav, Divider, Label, Toggle } from "../components/shared";

export default function AdminPortal({ leads, shops, settings, onUpdateLead, onUpdateShop, onUpdateSettings, onLogout }) {
  const [nav, setNav] = useState("qualified");
  const [tierTab, setTierTab] = useState("active");
  const [sheet, setSheet] = useState(null);
  const [localSettings, setLocalSettings] = useState(settings);
  // Keep localSettings in sync when Firestore settings update
  useState(() => { setLocalSettings(settings); }, [settings]);
  const [toast, setToast] = useState(null);
  const [overridePrice, setOverridePrice] = useState(15);
  const [newSheet, setNewSheet] = useState({ name:"", url:"" });
  const setSetting = (k,v) => setLocalSettings(s=>({...s,[k]:v}));

  // Always read sheets from settings (Firestore) — not local state
  const sheets = settings.googleSheets || [];

  const showToast = (msg, undoFn) => {
    setToast({ msg, undo:undoFn });
    setTimeout(()=>setToast(null), 6000);
  };

  const closeLead = (leadId, leadName) => {
    onUpdateLead(leadId,{status:"closed"});
    setSheet(null);
    showToast(`"${leadName}" lead closed`, ()=>onUpdateLead(leadId,{status:"new"}));
  };

  const reopenLead = (leadId, leadName) => {
    onUpdateLead(leadId,{status:"new"});
    showToast(`"${leadName}" lead reopened`, ()=>onUpdateLead(leadId,{status:"closed"}));
  };

  const approveShop = (shopId, shopName) => {
    onUpdateShop(shopId,{status:"active"});
    setSheet(null);
    showToast(`${shopName} approved ✓`, ()=>onUpdateShop(shopId,{status:"pending"}));
  };

  const rejectShop = (shopId, shopName) => {
    onUpdateShop(shopId,{status:"rejected"});
    setSheet(null);
    showToast(`${shopName} rejected`, ()=>onUpdateShop(shopId,{status:"pending"}));
  };

  const sortByNewest = arr => [...arr].sort((a,b)=>{
    const ta = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt ? new Date(a.createdAt) : new Date(0);
    const tb = b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt ? new Date(b.createdAt) : new Date(0);
    return tb - ta;
  });
  const newLeads = sortByNewest(leads.filter(l=>l.status==="new"));
  const closedLeads = sortByNewest(leads.filter(l=>l.status==="closed"));
  const premiumLeads = newLeads.filter(l=>l.tier==="premium");
  const qualifiedLeads = newLeads.filter(l=>l.tier==="qualified"||!l.tier);
  const bronzeLeads = newLeads.filter(l=>l.tier==="bronze");
  const closedPremium = closedLeads.filter(l=>l.tier==="premium");
  const closedQualified = closedLeads.filter(l=>l.tier==="qualified"||!l.tier);
  const closedBronze = closedLeads.filter(l=>l.tier==="bronze");
  const pendingShops = shops.filter(s=>s.status==="pending");
  const activeShops = shops.filter(s=>s.status==="active");
  const totalRev = leads.reduce((s,l)=>s+((l.buyers?.length||0)*(l.price||0)),0);

  const InfoRow = ({label,value,vc={}}) => (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderBottom:`1px solid ${C.border}`, fontSize:13 }}>
      <div style={{ color:C.muted }}>{label}</div><div style={{ textAlign:"right", color:C.text, ...vc }}>{value}</div>
    </div>
  );

  const isToday = (lead) => {
    const d = lead.createdAt?.toDate ? lead.createdAt.toDate() : lead.createdAt ? new Date(lead.createdAt) : null;
    if (!d) return false;
    const now = new Date();
    const ukNow = new Date(now.toLocaleString("en-GB", {timeZone:"Europe/London"}));
    const ukLead = new Date(d.toLocaleString("en-GB", {timeZone:"Europe/London"}));
    return ukNow.toDateString() === ukLead.toDateString();
  };

  const LeadCard = ({ lead, showClose=true, showReopen=false }) => (
    <div onClick={()=>{ setOverridePrice(lead.price||15); setSheet({type:"lead",lead}); }}
      style={{ background:C.card, border:`1px solid ${lead.budget?.includes("1,000")||lead.budget?.includes("5,000")?"#3a2a0a":C.border}`, borderRadius:14, marginBottom:12, overflow:"hidden", cursor:"pointer" }}>
      <div style={{ padding:"14px 16px", display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:15 }}>{lead.name}</div>
          <div style={{ fontSize:12, color:C.muted, marginTop:3 }}>{lead.vehicle} · {lead.createdAt?.toDate ? lead.createdAt.toDate().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : lead.createdAt ? new Date(lead.createdAt).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : "Recently"}</div>
        </div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {showReopen && <Badge color="grey">✓ Closed</Badge>}
          {!showReopen && isToday(lead) && <Badge color="green">● New</Badge>}
          {!showReopen && (lead.budget?.includes("1,000")||lead.budget?.includes("5,000")) && <Badge color="red">🔥 Hot</Badge>}
          {lead.tier==="bronze"&&<Badge color="amber">🥉 Bronze</Badge>}
          {lead.tier==="qualified"&&<Badge color="blue">🏠 Qualified</Badge>}
          {lead.tier==="premium"&&<Badge color="green">⭐ Premium</Badge>}
        </div>
      </div>
      <div style={{ padding:"0 16px 14px" }}>
        <div style={{ fontSize:13, color:"#AAAAAA", lineHeight:1.6, marginBottom:10 }}>{(lead.damage||"").slice(0,90)}...</div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {[lead.budget,lead.source,lead.photoCount>0?`📷 ${lead.photoCount}`:null].filter(Boolean).map(t=>(
            <span key={t} style={{ background:"#1a1a1a", border:`1px solid ${C.border}`, borderRadius:6, padding:"3px 8px", fontSize:11, color:C.muted }}>{t}</span>
          ))}
        </div>
      </div>
      <div style={{ padding:"12px 16px", borderTop:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ fontSize:14, fontWeight:800, color: showReopen?C.muted:C.greenLight }}>£{lead.price} · {lead.buyers?.length||0}/{lead.maxBuyers} sold</div>
        <div style={{ display:"flex", gap:6 }}>
          <Btn size="sm" variant="ghost">View</Btn>
          {showClose && <Btn size="sm" variant="danger" onClick={e=>{e.stopPropagation();closeLead(lead.id,lead.name);}}>Close</Btn>}
          {showReopen && <Btn size="sm" variant="ghost" onClick={e=>{e.stopPropagation();reopenLead(lead.id,lead.name);}}>Reopen</Btn>}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:C.dark, paddingBottom:80, color:C.text, fontFamily:"'DM Sans',sans-serif" }}>
      <Topbar right={<div style={{ background:"rgba(232,52,26,0.1)", border:"1px solid rgba(232,52,26,0.3)", color:C.red, fontSize:11, padding:"3px 10px", borderRadius:20, fontWeight:700 }}>🔐 Admin</div>} />

      {/* Undo Toast */}
      {toast && (
        <div style={{ position:"fixed", bottom:90, left:16, right:16, zIndex:300 }}>
          <div style={{ background:"#1a1a1a", border:`1px solid ${C.border}`, borderRadius:14, padding:"13px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, boxShadow:"0 8px 32px rgba(0,0,0,0.6)" }}>
            <span style={{ fontSize:13, color:C.text, flex:1 }}>{toast.msg}</span>
            <button onClick={()=>{ toast.undo(); setToast(null); }} style={{ background:C.red, color:"#fff", border:"none", borderRadius:8, padding:"6px 14px", fontFamily:"inherit", fontSize:12, fontWeight:700, cursor:"pointer" }}>Undo</button>
          </div>
        </div>
      )}

      {/* ACTIVE LEADS */}
      {(nav==="premium"||nav==="qualified"||nav==="bronze")&&(()=>{
        const tierLeads = nav==="premium" ? premiumLeads : nav==="qualified" ? qualifiedLeads : bronzeLeads;
        const closedTierLeads = nav==="premium" ? closedPremium : nav==="qualified" ? closedQualified : closedBronze;
        const tierLabel = nav==="premium" ? "⭐ Verified Premium" : nav==="qualified" ? "🏠 Qualified" : "🥉 Bronze";
        const tierColor = nav==="premium" ? "#f59e0b" : nav==="qualified" ? C.blue : "#92400e";
        const showClosed = tierTab === "closed";
        return (
          <div style={{ padding:16 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16, marginTop:4 }}>
              <Stat label="Active" value={tierLeads.length} sub={tierLabel} accent={tierColor} />
              <Stat label="Closed" value={closedTierLeads.length} sub="This tier" accent={C.muted} />
              <Stat label="Revenue" value={`£${[...tierLeads,...closedTierLeads].reduce((s,l)=>s+((l.buyers?.length||0)*(l.price||0)),0)}`} sub="All time" accent={C.greenLight} />
              <Stat label="Sell Rate" value={`${[...tierLeads,...closedTierLeads].length?Math.round([...tierLeads,...closedTierLeads].filter(l=>(l.buyers?.length||0)>0).length/[...tierLeads,...closedTierLeads].length*100):0}%`} sub="purchased" accent={C.amber} />
            </div>
            <div style={{ display:"flex", gap:8, marginBottom:16 }}>
              {[["active","Active"],["closed","Closed"]].map(([id,lbl])=>(
                <div key={id} onClick={()=>setTierTab(id)} style={{ padding:"6px 14px", borderRadius:20, fontSize:12, fontWeight:700, cursor:"pointer", border:`1px solid ${tierTab===id?C.red:C.border}`, background:tierTab===id?C.red:C.card, color:tierTab===id?"#fff":C.muted }}>{lbl}</div>
              ))}
            </div>
            {!showClosed&&(tierLeads.length===0
              ? <div style={{ textAlign:"center", padding:"40px 20px", color:C.muted, fontSize:14 }}>No active {tierLabel} leads.</div>
              : tierLeads.map(lead=><LeadCard key={lead.id} lead={lead} showClose={true} />)
            )}
            {showClosed&&(closedTierLeads.length===0
              ? <div style={{ textAlign:"center", padding:"40px 20px", color:C.muted, fontSize:14 }}>No closed {tierLabel} leads.</div>
              : closedTierLeads.map(lead=><div key={lead.id} style={{ opacity:0.75 }}><LeadCard lead={lead} showClose={false} showReopen={true} /></div>)
            )}
          </div>
        );
      })()}

      {/* SHOPS */}
      {nav==="shops"&&(
        <div style={{ padding:16 }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:16, marginTop:4 }}>
            <Stat label="Active" value={activeShops.length} accent={C.greenLight} />
            <Stat label="Pending" value={pendingShops.length} accent={C.amber} />
            <Stat label="Total" value={shops.length} accent={C.blue} />
          </div>
          {shops.map(shop=>(
            <div key={shop.id} onClick={()=>setSheet({type:"shop",shop})} style={{ background:C.card, border:`1px solid ${shop.status==="pending"?"#3a3a08":C.border}`, borderRadius:14, padding:16, marginBottom:12, cursor:"pointer" }}>
              <div style={{ display:"flex", alignItems:"flex-start", gap:12, marginBottom:12 }}>
                <div style={{ width:42, height:42, borderRadius:"50%", background: shop.status==="pending"?"rgba(245,158,11,0.15)":"rgba(232,52,26,0.15)", color: shop.status==="pending"?C.amber:C.red, fontWeight:800, fontSize:13, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  {(shop.businessName||"").slice(0,2).toUpperCase()}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:15 }}>{shop.businessName}</div>
                  <div style={{ fontSize:12, color:C.muted, marginTop:3 }}>{shop.postcode} · {shop.status==="pending"?`Applied`:`Active`}</div>
                </div>
                <Badge color={shop.status==="active"?"green":shop.status==="rejected"?"danger":"amber"}>
                  {shop.status==="active"?"● Active":shop.status==="rejected"?"✗ Rejected":"⏳ Pending"}
                </Badge>
              </div>
              <div style={{ fontSize:12, color:C.muted, marginBottom:12 }}>
                {shop.status==="active" ? `${leads.filter(l=>l.buyers?.includes(shop.id)).length} leads bought · £${leads.filter(l=>l.buyers?.includes(shop.id)).reduce((s,l)=>s+(l.price||0),0)} spent` : `CH: ${shop.companiesHouse} · ${shop.email}`}
              </div>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", paddingTop:12, borderTop:`1px solid ${C.border}` }}>
                <div style={{ fontSize:11, color:C.muted }}>{shop.phone}</div>
                <div style={{ display:"flex", gap:6 }}>
                  {shop.status==="pending"?(<>
                    <Btn size="sm" variant="red" onClick={e=>{e.stopPropagation();approveShop(shop.id,shop.businessName);}}>Approve</Btn>
                    <Btn size="sm" variant="danger" onClick={e=>{e.stopPropagation();rejectShop(shop.id,shop.businessName);}}>Reject</Btn>
                  </>):(<Btn size="sm" variant="blue" onClick={e=>{e.stopPropagation();setSheet({type:"shop",shop});}}>View</Btn>)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* REVENUE */}
      {nav==="revenue"&&(
        <div style={{ padding:16 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16, marginTop:4 }}>
            <Stat label="Total Revenue" value={`£${totalRev}`} sub="All time" accent={C.greenLight} />
            <Stat label="Leads Sold" value={leads.filter(l=>(l.buyers?.length||0)>0).length} sub={`of ${leads.length} total`} accent={C.blue} />
          </div>
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, overflow:"hidden" }}>
            <div style={{ padding:"14px 16px 12px", fontSize:13, fontWeight:800, borderBottom:`1px solid ${C.border}` }}>Top shops by spend</div>
            {activeShops.length===0&&<div style={{ padding:"20px 16px", fontSize:13, color:C.muted }}>No active shops yet.</div>}
            {activeShops.map(shop=>{
              const spent=leads.filter(l=>l.buyers?.includes(shop.id)).reduce((s,l)=>s+(l.price||0),0);
              return (<div key={shop.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 16px", borderBottom:`1px solid ${C.border}`, fontSize:13 }}><div>{shop.businessName}</div><div style={{ fontWeight:800, color:C.greenLight }}>£{spent}</div></div>);
            })}
          </div>
        </div>
      )}

      {/* SETTINGS */}
      {nav==="settings"&&(
        <div style={{ padding:16 }}>
          {/* Pixel */}
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, marginBottom:14, overflow:"hidden" }}>
            <div style={{ padding:"14px 16px 12px", fontSize:14, fontWeight:800, borderBottom:`1px solid ${C.border}` }}>📡 Meta (Facebook) Pixel</div>
            <div style={{ padding:16 }}>
              <div style={{ fontSize:13, color:"#AAAAAA", lineHeight:1.7, marginBottom:16 }}>Paste your Pixel ID to automatically track <strong style={{ color:C.text }}>PageView</strong>, <strong style={{ color:C.text }}>ViewContent</strong> and <strong style={{ color:C.text }}>Lead</strong> events on your customer form.</div>
              <Label>Meta Pixel ID</Label>
              <input value={localSettings.pixelId||""} onChange={e=>setSetting("pixelId",e.target.value)} placeholder="e.g. 1304664438459169"
                style={{ width:"100%", background:"#0D0D0D", border:`1.5px solid ${C.border}`, borderRadius:12, padding:"13px 16px", color:C.text, fontFamily:"inherit", fontSize:14, outline:"none", boxSizing:"border-box" }} />
              <div style={{ fontSize:11, color:C.muted, marginTop:6, marginBottom:16 }}>Find in: Meta Business Manager → Events Manager → Your Pixel → Settings</div>
              {localSettings.pixelId&&(
                <div style={{ background:"rgba(22,163,74,0.08)", border:"1px solid rgba(22,163,74,0.2)", borderRadius:10, padding:"10px 14px", display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:C.greenLight, flexShrink:0 }} />
                  <div style={{ fontSize:12, color:C.greenLight, fontWeight:700 }}>Pixel active · ID: {localSettings.pixelId}</div>
                </div>
              )}
              <Btn onClick={()=>{ onUpdateSettings(localSettings); alert("✅ Pixel saved!"); }} variant="red" size="lg" full>Save & Activate Pixel</Btn>
            </div>
          </div>

          {/* Pricing */}
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, marginBottom:14, overflow:"hidden" }}>
            <div style={{ padding:"14px 16px 12px", fontSize:14, fontWeight:800, borderBottom:`1px solid ${C.border}` }}>💰 Pricing & Lead Limits</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, padding:16 }}>
              {[["defaultPrice","Qualified price","£"],["bronzePrice","Bronze price","£"],["premiumPrice","Premium price","£"],["maxBuyers","Max shops / lead","#"],["priceBodywork","Bodywork & Paint","£"],["pricePartial","Partial Restore","£"],["priceFull","Full Restoration","£"],["priceHighBudget","Budget £1k+","£"]].map(([k,lbl,pre])=>(
                <div key={k}><Label>{lbl}</Label>
                  <div style={{ display:"flex" }}>
                    <div style={{ background:"#1a1a1a", border:`1px solid ${C.border}`, borderRight:"none", borderRadius:"9px 0 0 9px", padding:"10px 12px", color:C.muted, fontSize:13 }}>{pre}</div>
                    <input type="number" value={localSettings[k]||0} onChange={e=>setSetting(k,Number(e.target.value))} style={{ border:`1px solid ${C.border}`, background:"#0D0D0D", color:C.text, borderRadius:"0 9px 9px 0", padding:"10px 12px", fontSize:14, fontWeight:800, fontFamily:"inherit", outline:"none", width:70, boxSizing:"border-box" }} />
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding:"0 16px 16px" }}><Btn onClick={()=>{ onUpdateSettings(localSettings); alert("✅ Pricing saved!"); }} variant="red" size="lg" full>Save Pricing</Btn></div>
          </div>

          {/* Platform */}
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, marginBottom:14, overflow:"hidden" }}>
            <div style={{ padding:"14px 16px 12px", fontSize:14, fontWeight:800, borderBottom:`1px solid ${C.border}` }}>⚙ Platform Controls</div>
            {[["alertNewLead","New lead alerts","Email when a lead comes in"],["autoClose30","Auto-close after 30 days","Unsold leads close automatically"],["requirePhotos","Require minimum 3 photos","Block form without photos"],["manualApproval","Manual shop approval","You approve every new bodyshop"]].map(([k,lbl,sub])=>(
              <div key={k} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"13px 16px", borderBottom:`1px solid ${C.border}`, gap:12 }}>
                <div><div style={{ fontSize:13, fontWeight:500 }}>{lbl}</div><div style={{ fontSize:11, color:C.muted, marginTop:3 }}>{sub}</div></div>
                <Toggle checked={!!localSettings[k]} onChange={v=>setSetting(k,v)} />
              </div>
            ))}
          </div>

          {/* Google Sheets */}
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, marginBottom:14, overflow:"hidden" }}>
            <div style={{ padding:"14px 16px 12px", fontSize:14, fontWeight:800, borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span>📊 Facebook Lead Sources (Google Sheets)</span>
              <Btn size="sm" variant="red" onClick={async ()=>{ 
                try { 
                  const r = await fetch("https://quotemybodywork-api.vercel.app/api/syncSheets", {method:"POST", headers:{"Content-Type":"application/json"}, body:"{}"}); 
                  const d = await r.json(); 
                  showToast(d.message || "Sync complete ✓"); 
                } catch(e) { 
                  alert("Sync failed: " + e.message); 
                } 
              }}>🔄 Sync Now</Btn>
            </div>
            <div style={{ padding:16 }}>
              <div style={{ fontSize:12, color:C.muted, marginBottom:14, lineHeight:1.6 }}>Connect Google Sheets from your Facebook Lead Ads. New rows automatically appear as Bronze leads in the app.</div>
              {sheets.map((s,i)=>(
                <div key={i} style={{ background:"#111", border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px", marginBottom:10, display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{s.name}</div>
                    <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{s.url.slice(0,40)}...</div>
                  </div>
                  <Btn size="sm" variant="danger" onClick={()=>{ const updated=sheets.filter((_,j)=>j!==i); onUpdateSettings({...localSettings,googleSheets:updated}); showToast("Sheet removed"); }}>Remove</Btn>
                </div>
              ))}
              {sheets.length===0&&<div style={{ fontSize:13, color:C.muted, textAlign:"center", padding:"20px 0" }}>No sheets connected yet</div>}
              <div style={{ background:"#111", border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px", marginTop:10 }}>
                <Label>Sheet name (e.g. Manchester Ads)</Label>
                <input value={newSheet.name} onChange={e=>setNewSheet(s=>({...s,name:e.target.value}))} placeholder="Manchester Ads" style={{ width:"100%", background:"#0D0D0D", border:`1px solid ${C.border}`, borderRadius:9, padding:"10px 12px", color:C.text, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box", marginBottom:10 }} />
                <Label>Google Sheet URL</Label>
                <input value={newSheet.url} onChange={e=>setNewSheet(s=>({...s,url:e.target.value}))} placeholder="https://docs.google.com/spreadsheets/d/..." style={{ width:"100%", background:"#0D0D0D", border:`1px solid ${C.border}`, borderRadius:9, padding:"10px 12px", color:C.text, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box", marginBottom:10 }} />
                <Btn variant="red" full onClick={()=>{ if(!newSheet.name||!newSheet.url){alert("Please enter both name and URL");return;} const updated=[...sheets,{...newSheet,connected:new Date().toISOString()}]; onUpdateSettings({...localSettings,googleSheets:updated}); setNewSheet({name:"",url:""}); showToast("Sheet connected ✓"); }}>+ Add Sheet</Btn>
              </div>
            </div>
          </div>

          <Btn onClick={onLogout} variant="ghost" size="lg" full>Sign out</Btn>
        </div>
      )}

      <BottomNav active={nav} onSelect={setNav} tabs={[
        { id:"premium", icon:"⭐", label:"Premium", count:premiumLeads.length },
        { id:"qualified", icon:"🏠", label:"Qualified", count:qualifiedLeads.length },
        { id:"bronze", icon:"🥉", label:"Bronze", count:bronzeLeads.length },
        { id:"shops", icon:"🏭", label:"Shops", count:pendingShops.length },
        { id:"settings", icon:"⚙️", label:"Settings", count:0 },
      ]} />

      {/* Lead detail sheet */}
      <Sheet open={sheet?.type==="lead"} onClose={()=>setSheet(null)}>
        {sheet?.type==="lead"&&(()=>{
          const l=sheet.lead;
          return (<>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
              <div style={{ fontSize:19, fontWeight:900 }}>{l.name}</div>
              <Badge color={l.status==="closed"?"grey":l.budget?.includes("1,000")||l.budget?.includes("5,000")?"red":"green"}>{l.status==="closed"?"Closed":l.budget?.includes("1,000")||l.budget?.includes("5,000")?"🔥 Hot":"● New"}</Badge>
            </div>
            <div style={{ fontSize:12, color:C.muted, marginBottom:20 }}>{l.source} · {l.id}</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
              {[["Vehicle",l.vehicle],["Budget",l.budget],["Work type",l.workType],["Timeline",l.timeline],["Ready to book?",l.dropoff],["Quoted elsewhere?",l.quotedElsewhere],["Best call time",l.callTime],["Photos",`${l.photoCount||0} uploaded`]].map(([k,v])=>(
                <div key={k}><Label>{k}</Label><div style={{ fontSize:13, color:C.text }}>{v}</div></div>
              ))}
              <div style={{ gridColumn:"1/-1" }}><Label>Damage description</Label><div style={{ fontSize:13, color:C.text, lineHeight:1.6 }}>{l.damage}</div></div>
            </div>
            {l.photoUrls?.length>0&&(
              <div style={{ marginBottom:14 }}>
                <Label>Photos</Label>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
                  {l.photoUrls.map((url,i)=>(<div key={i} style={{ aspectRatio:1, borderRadius:10, overflow:"hidden", border:`1px solid ${C.border}` }}><img src={url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /></div>))}
                </div>
              </div>
            )}
            <Divider />
            <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:"0.7px", color:C.red, marginBottom:10, fontWeight:800 }}>🔐 Admin only — customer contact</div>
            <div style={{ background:"rgba(22,163,74,0.06)", border:"1px solid rgba(22,163,74,0.2)", borderRadius:14, padding:16, marginBottom:14 }}>
              {[["Full name",l.name],["Phone",l.phone],["WhatsApp",l.whatsapp||l.phone],["Email",l.email]].map(([k,v])=>(
                <div key={k} style={{ marginBottom:10 }}><div style={{ fontSize:10, color:C.greenLight, fontWeight:700, marginBottom:3 }}>{k}</div><div style={{ fontSize:15, fontWeight:800, color:C.greenLight }}>{v}</div></div>
              ))}
            </div>
            <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:"0.7px", color:C.red, marginBottom:10, fontWeight:800 }}>🔐 Admin only — price override</div>
            <div style={{ background:"#111", border:`1px solid ${C.border}`, borderRadius:12, padding:"14px 16px", marginBottom:14 }}>
              <Label>Lead Tier</Label>
              <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
                {[["bronze","🥉 Bronze",settings.bronzePrice||8],["qualified","🏠 Qualified",settings.defaultPrice||15],["premium","⭐ Verified Premium",settings.premiumPrice||25]].map(([t,lbl,defaultPrice])=>(
                  <div key={t} onClick={()=>{ onUpdateLead(l.id,{tier:t, price:defaultPrice}); setOverridePrice(defaultPrice); showToast(`Tier updated to ${lbl} · Price set to £${defaultPrice}`); }} style={{ padding:"7px 14px", borderRadius:20, fontSize:12, fontWeight:700, cursor:"pointer", border:`1px solid ${(l.tier||"qualified")===t?C.red:C.border}`, background:(l.tier||"qualified")===t?C.red:"#1a1a1a", color:"#fff" }}>{lbl} — £{defaultPrice}</div>
                ))}
              </div>
              <Label>Admin note (visible to bodyshops)</Label>
              <textarea
                defaultValue={l.adminNote||""}
                placeholder="e.g. Customer expects £300-400, very keen to book this week"
                onBlur={e=>{ if(e.target.value!==(l.adminNote||"")) { onUpdateLead(l.id,{adminNote:e.target.value}); showToast("Note saved ✓"); } }}
                style={{ width:"100%", background:"#0D0D0D", border:`1px solid ${C.border}`, borderRadius:9, padding:"10px 12px", color:C.text, fontSize:13, fontFamily:"inherit", outline:"none", resize:"vertical", minHeight:80, boxSizing:"border-box", marginBottom:14 }}
              />
              <Label>Override price for this lead</Label>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:12, color:C.muted, textDecoration:"line-through", marginRight:4 }}>£{settings.defaultPrice} default</span>
                <div style={{ display:"flex" }}>
                  <div style={{ background:"#1a1a1a", border:`1px solid ${C.border}`, borderRight:"none", borderRadius:"9px 0 0 9px", padding:"9px 12px", color:C.muted }}>£</div>
                  <input type="number" value={overridePrice} onChange={e=>setOverridePrice(Number(e.target.value))} style={{ border:`1px solid ${C.border}`, background:"#0D0D0D", color:C.text, borderRadius:"0 9px 9px 0", padding:"9px 12px", fontSize:14, fontWeight:800, fontFamily:"inherit", outline:"none", width:80 }} />
                </div>
                <Btn size="sm" variant="red" onClick={()=>{ onUpdateLead(l.id,{price:overridePrice}); showToast("Price updated to £"+overridePrice, ()=>onUpdateLead(l.id,{price:l.price})); }}>Save</Btn>
              </div>
            </div>
            <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:"0.7px", color:C.red, marginBottom:10, fontWeight:800 }}>🔐 Admin only — who bought this lead</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:16 }}>
              {(l.buyers||[]).map(bid=>{ const bs=shops.find(s=>s.id===bid); return <div key={bid} style={{ background:"rgba(22,163,74,0.08)", border:"1px solid rgba(22,163,74,0.25)", borderRadius:9, padding:"6px 12px", fontSize:12, color:C.greenLight, fontWeight:600 }}>✓ {bs?.businessName||bid} · £{l.price}</div>; })}
              {Array.from({length:Math.max(0,(l.maxBuyers||3)-(l.buyers?.length||0))}).map((_,i)=>(<div key={i} style={{ background:"#111", border:`1px dashed ${C.border}`, borderRadius:9, padding:"6px 12px", fontSize:12, color:C.muted }}>Slot {(l.buyers?.length||0)+i+1} open</div>))}
            </div>
            {l.status!=="closed"&&<Btn onClick={()=>closeLead(l.id,l.name)} variant="danger" size="lg" full>Close this lead</Btn>}
            {l.status==="closed"&&<Btn onClick={()=>{ reopenLead(l.id,l.name); setSheet(null); }} variant="ghost" size="lg" full>Reopen this lead</Btn>}
          </>);
        })()}
      </Sheet>

      {/* Shop detail sheet */}
      <Sheet open={sheet?.type==="shop"} onClose={()=>setSheet(null)}>
        {sheet?.type==="shop"&&(()=>{
          const s=sheet.shop;
          const shopLeads=leads.filter(l=>l.buyers?.includes(s.id));
          const spent=shopLeads.reduce((sum,l)=>sum+(l.price||0),0);
          return (<>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
              <div style={{ fontSize:19, fontWeight:900 }}>{s.businessName}</div>
              <Badge color={s.status==="active"?"green":s.status==="rejected"?"danger":"amber"}>{s.status==="active"?"● Active":s.status==="rejected"?"✗ Rejected":"⏳ Pending"}</Badge>
            </div>
            <div style={{ fontSize:12, color:C.muted, marginBottom:20 }}>{s.postcode} · {s.status}</div>
            <div style={{ background:"#111", border:`1px solid ${C.border}`, borderRadius:14, padding:16, marginBottom:16 }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:12 }}>Account details</div>
              <InfoRow label="Owner" value={s.ownerName} />
              <InfoRow label="Postcode" value={s.postcode} />
              <InfoRow label="Companies House" value={s.companiesHouse} />
              <InfoRow label="Phone" value={s.phone} />
              <InfoRow label="Email" value={s.email} />
              <InfoRow label="Radius" value={`${s.radius} miles`} />
              {s.status==="active"&&<>
                <InfoRow label="Leads bought" value={shopLeads.length} vc={{ color:C.greenLight, fontWeight:700 }} />
                <InfoRow label="Total spent" value={`£${spent}`} vc={{ color:C.greenLight, fontWeight:700 }} />
              </>}
              <InfoRow label="Card details" value="Held by Stripe — not visible ✓" vc={{ color:C.muted }} />
            </div>
            {s.status==="pending"&&(<>
              <a href={`https://find-and-update.company-information.service.gov.uk/search?q=${s.companiesHouse}`} target="_blank" rel="noreferrer" style={{ display:"block", padding:"13px 16px", borderRadius:12, background:"rgba(59,130,246,0.08)", border:"1px solid rgba(59,130,246,0.25)", color:C.blue, fontSize:13, textAlign:"center", textDecoration:"none", fontWeight:700, marginBottom:14 }}>🔍 Verify on Companies House →</a>
              <div style={{ display:"flex", gap:8 }}>
                <Btn onClick={()=>approveShop(s.id,s.businessName)} variant="red" size="lg" full>✓ Approve</Btn>
                <Btn onClick={()=>rejectShop(s.id,s.businessName)} variant="danger" size="lg" full>✗ Reject</Btn>
              </div>
            </>)}
            {s.status==="active"&&<Btn onClick={()=>{ onUpdateShop(s.id,{status:"suspended"}); setSheet(null); showToast(`${s.businessName} suspended`, ()=>onUpdateShop(s.id,{status:"active"})); }} variant="danger" size="lg" full style={{ marginTop:8 }}>Suspend shop</Btn>}
          </>);
        })()}
      </Sheet>
    </div>
  );
}
